import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { t } from 'i18next';
import { FiPaperclip, FiFileText, FiChevronRight, FiEdit3, FiCheckCircle } from 'react-icons/fi';
import { Badge } from '@/components/ui/badge';
import { IconVerified } from '@/components/custom/icon';
import { PATHS } from '@/routes/paths';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Loader } from '@/components/custom/loader';
import { cn } from '@/lib/utils';

// Card lúdico de módulo compartilhado entre aluno (CourseMonitoring) e
// professor (CourseAbout). Não conhece papéis/permissões — cada tela decide o
// que exibir via props (progress/locked pro aluno, management pro professor).
export function CourseModulePlayfulCard(props) {
  const {
    moduleId,
    title,
    officialContent = false,
    onClick,
    index = 0,
    badge,
    lessonsCount,
    progress,
    management,
    locked,
    cta,
    actions,
    filesCount = 0,
    activitiesCount = 0,
    bookletsCount = 0,
    details,
    isLoadingDetails = false,
    onRequestDetails = () => {},
    // Aluno e professor acessam uma atividade por rotas diferentes (aluno
    // responde, professor acompanha submissões). Por padrão aponta pra rota do
    // aluno; quem chama pelo lado do professor passa a rota certa.
    activityHref = activity => PATHS.COMPLEMENTARY_ACTIVITY.DETAIL(activity.id)
  } = props;

  const [showFiles, setShowFiles] = useState(false);
  const [showActivities, setShowActivities] = useState(false);
  const [showBooklets, setShowBooklets] = useState(false);

  // O detalhamento (lista de arquivos/atividades/apostilas) só é buscado no
  // clique de algum dos botões; até chegar, o modal mostra um loading.
  const files = details?.files ?? [];
  const activities = details?.activities ?? [];
  const booklets = details?.booklets ?? [];
  const isLoadingModuleDetails = isLoadingDetails || !details;

  const percent = progress?.totalCount ? Math.round((progress.finishedCount / progress.totalCount) * 100) : 0;

  return (
    <>
      <article
        onClick={onClick}
        className="flex cursor-pointer flex-col gap-3 rounded-[16px] border border-neutral-100 bg-white p-4 shadow-card transition-[box-shadow,transform] duration-150 hover:-translate-y-0.5 hover:shadow-card-hover lg:flex-row lg:flex-wrap lg:items-center lg:gap-4"
        style={{ animation: `trilha-in 380ms cubic-bezier(0.23, 1, 0.32, 1) ${250 + index * 70}ms both` }}
      >
        {/* Até o tablet (abaixo do lg): nome do módulo + opções numa linha, e
            os demais botões isolados na linha de baixo, quebrando entre si. A
            partir do lg os dois wrappers "somem" (contents) e tudo volta a
            ficar numa linha só, como era antes — opções por último. */}
        <div className="flex items-start justify-between gap-3 lg:contents">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              {officialContent && <IconVerified />}
              <h3
                className="mb-0 text-base font-extrabold leading-snug"
                style={{ color: '#222731' }}
              >
                {title}
              </h3>
              {badge?.text && (
                <Badge
                  variant={badge.variant}
                  className="shrink-0 rounded-full px-2.5 py-1 text-xs font-bold"
                >
                  {badge.text}
                </Badge>
              )}
              {typeof lessonsCount === 'number' && (
                <span className="whitespace-nowrap text-xs font-bold text-neutral-500">
                  {lessonsCount} {t('lms.lms')}
                </span>
              )}
              {management && <span onClick={event => event.stopPropagation()}>{management}</span>}
            </div>

            {progress && (
              <div className="mt-2 flex items-center gap-3">
                <div className="h-2 w-full max-w-[220px] overflow-hidden rounded-full bg-[var(--color-bg-lilac)]">
                  <div
                    className="h-full rounded-full"
                    style={{ width: `${Math.min(percent, 100)}%`, background: 'var(--gradient-progress-bar)' }}
                  />
                </div>
                <span className="whitespace-nowrap text-xs font-bold text-neutral-500">
                  {progress.finishedCount}/{progress.totalCount} {t('lms.lms')}
                </span>
              </div>
            )}

            {progress?.disclaimer && <p className="mb-0 mt-2 text-xs font-semibold leading-relaxed text-neutral-500">Parabéns, todas as aulas foram concluídas! Fica de olho: mais conteúdos podem chegar em breve. 👀</p>}
          </div>

          {actions && (
            <span
              className="shrink-0 lg:hidden"
              onClick={event => event.stopPropagation()}
            >
              {actions}
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3 lg:contents">
          {filesCount > 0 && (
            <button
              type="button"
              onClick={event => {
                event.stopPropagation();
                onRequestDetails();
                setShowFiles(true);
              }}
              className="flex shrink-0 items-center gap-1.5 rounded-full border-2 border-[var(--color-bg-lilac)] bg-white px-4 py-2 text-sm font-bold text-[var(--color-secondary)] transition-[border-color,transform] duration-100 hover:border-[var(--color-secondary)] active:scale-[0.97]"
            >
              <FiPaperclip className="h-4 w-4" /> Arquivos
              <span
                className="flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-xs font-extrabold text-white"
                style={{ background: 'var(--color-secondary)' }}
              >
                {filesCount}
              </span>
            </button>
          )}

          {activitiesCount > 0 && (
            <button
              type="button"
              onClick={event => {
                event.stopPropagation();
                onRequestDetails();
                setShowActivities(true);
              }}
              className="flex shrink-0 items-center gap-1.5 rounded-full border-2 border-[var(--color-bg-lilac)] bg-white px-4 py-2 text-sm font-bold text-[var(--color-secondary)] transition-[border-color,transform] duration-100 hover:border-[var(--color-secondary)] active:scale-[0.97]"
            >
              <FiCheckCircle className="h-4 w-4" /> Atividades
              <span
                className="flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-xs font-extrabold text-white"
                style={{ background: 'var(--color-secondary)' }}
              >
                {activitiesCount}
              </span>
            </button>
          )}

          {bookletsCount > 0 && (
            <button
              type="button"
              onClick={event => {
                event.stopPropagation();
                onRequestDetails();
                setShowBooklets(true);
              }}
              className="flex shrink-0 items-center gap-1.5 rounded-full border-2 border-[var(--color-bg-lilac)] bg-white px-4 py-2 text-sm font-bold text-[var(--color-secondary)] transition-[border-color,transform] duration-100 hover:border-[var(--color-secondary)] active:scale-[0.97]"
            >
              <FiEdit3 className="h-4 w-4" /> Exercícios
              <span
                className="flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-xs font-extrabold text-white"
                style={{ background: 'var(--color-secondary)' }}
              >
                {bookletsCount}
              </span>
            </button>
          )}

          {locked ? (
            <span className="shrink-0 cursor-not-allowed rounded-full border border-neutral-200 bg-neutral-100 px-6 py-2.5 text-center text-sm font-bold text-neutral-400">{locked.message}</span>
          ) : (
            cta && (
              <button
                type="button"
                disabled={cta.disabled}
                onClick={event => {
                  event.stopPropagation();
                  if (cta.disabled) return;
                  cta.onClick?.();
                }}
                className={cn(
                  'relative shrink-0 overflow-hidden rounded-full px-8 py-2.5 text-center text-sm font-extrabold transition-transform duration-100 active:scale-[0.97]',
                  cta.disabled ? 'cursor-not-allowed bg-neutral-100 text-neutral-400' : 'text-white'
                )}
                style={cta.disabled ? undefined : { background: 'var(--gradient-btn-start)', boxShadow: '0 4px 0 var(--color-secondary-dark), 0 6px 12px rgba(68,43,140,0.35)' }}
              >
                {!cta.disabled && (
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-t-full"
                    style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.18) 0%, transparent 100%)' }}
                  />
                )}
                <span className="relative">{cta.label}</span>
              </button>
            )
          )}

          {actions && (
            <span
              className="hidden shrink-0 lg:inline-flex"
              onClick={event => event.stopPropagation()}
            >
              {actions}
            </span>
          )}
        </div>
      </article>

      {/* Modal com os arquivos do módulo. Fica fora do <article> para o clique
        não borbulhar pro onClick de navegação do card. */}
      {filesCount > 0 && (
        <Dialog
          open={showFiles}
          onOpenChange={setShowFiles}
        >
          <DialogContent
            size="sm"
            hideCloseButton
            className="w-[420px] gap-0 rounded-[24px] border-0 p-0 sm:rounded-[24px]"
          >
            <div
              className="rounded-t-[24px] px-6 pb-4 pt-6"
              style={{ background: 'var(--color-bg-lilac)' }}
            >
              <DialogTitle
                className="mb-1 text-lg font-extrabold"
                style={{ color: '#222731' }}
              >
                📎 Arquivos do módulo
              </DialogTitle>
              <DialogDescription className="mb-0 text-sm font-semibold text-neutral-500">{title}</DialogDescription>
            </div>

            <div className="flex max-h-[50vh] flex-col gap-2 overflow-y-auto p-5">
              {isLoadingModuleDetails ? (
                <Loader />
              ) : (
                files.map(file => (
                  <Link
                    key={file.id}
                    to={PATHS.MATERIALS.DETAIL(file.id, moduleId)}
                    className="group flex items-center gap-3 rounded-[12px] border border-neutral-100 p-3 transition-colors duration-150 hover:border-neutral-200 hover:bg-neutral-50"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--color-bg-lilac)]">
                      <FiFileText className="h-4 w-4 text-[var(--color-secondary)]" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span
                        className="block truncate text-sm font-bold"
                        style={{ color: '#222731' }}
                      >
                        {file.title}
                      </span>
                      {file['file-size'] && <span className="text-xs font-semibold text-neutral-400">{file['file-size']}</span>}
                    </span>
                    <FiChevronRight className="h-4 w-4 shrink-0 text-neutral-300 transition-transform duration-150 group-hover:translate-x-0.5" />
                  </Link>
                ))
              )}
            </div>

            <div className="px-5 pb-5">
              <button
                type="button"
                onClick={() => setShowFiles(false)}
                className="w-full rounded-full py-2.5 text-sm font-bold text-neutral-400 transition-colors duration-150 hover:text-neutral-600"
              >
                Fechar
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Modal com as atividades do módulo. */}
      {activitiesCount > 0 && (
        <Dialog
          open={showActivities}
          onOpenChange={setShowActivities}
        >
          <DialogContent
            size="sm"
            hideCloseButton
            className="w-[420px] gap-0 rounded-[24px] border-0 p-0 sm:rounded-[24px]"
          >
            <div
              className="rounded-t-[24px] px-6 pb-4 pt-6"
              style={{ background: 'var(--color-bg-lilac)' }}
            >
              <DialogTitle
                className="mb-1 text-lg font-extrabold"
                style={{ color: '#222731' }}
              >
                📝 Atividades do módulo
              </DialogTitle>
              <DialogDescription className="mb-0 text-sm font-semibold text-neutral-500">{title}</DialogDescription>
            </div>

            <div className="flex max-h-[50vh] flex-col gap-2 overflow-y-auto p-5">
              {isLoadingModuleDetails ? (
                <Loader />
              ) : (
                activities.map(activity => (
                  <Link
                    key={activity.id}
                    to={activityHref(activity)}
                    className="group flex items-center gap-3 rounded-[12px] border border-neutral-100 p-3 transition-colors duration-150 hover:border-neutral-200 hover:bg-neutral-50"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--color-bg-lilac)]">
                      <FiCheckCircle className="h-4 w-4 text-[var(--color-secondary)]" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span
                        className="block truncate text-sm font-bold"
                        style={{ color: '#222731' }}
                      >
                        {activity.title}
                      </span>
                      {activity?.status && <span className="text-xs font-semibold text-neutral-400">{activity.status}</span>}
                    </span>
                    <FiChevronRight className="h-4 w-4 shrink-0 text-neutral-300 transition-transform duration-150 group-hover:translate-x-0.5" />
                  </Link>
                ))
              )}
            </div>

            <div className="px-5 pb-5">
              <button
                type="button"
                onClick={() => setShowActivities(false)}
                className="w-full rounded-full py-2.5 text-sm font-bold text-neutral-400 transition-colors duration-150 hover:text-neutral-600"
              >
                Fechar
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Modal com as apostilas do módulo. */}
      {bookletsCount > 0 && (
        <Dialog
          open={showBooklets}
          onOpenChange={setShowBooklets}
        >
          <DialogContent
            size="sm"
            hideCloseButton
            className="w-[420px] gap-0 rounded-[24px] border-0 p-0 sm:rounded-[24px]"
          >
            <div
              className="rounded-t-[24px] px-6 pb-4 pt-6"
              style={{ background: 'var(--color-bg-lilac)' }}
            >
              <DialogTitle
                className="mb-1 text-lg font-extrabold"
                style={{ color: '#222731' }}
              >
                📚 Exercícios do módulo
              </DialogTitle>
              <DialogDescription className="mb-0 text-sm font-semibold text-neutral-500">{title}</DialogDescription>
            </div>

            <div className="flex max-h-[50vh] flex-col gap-2 overflow-y-auto p-5">
              {isLoadingModuleDetails ? (
                <Loader />
              ) : (
                booklets.map(booklet => (
                  <Link
                    key={booklet.id}
                    to={PATHS.QUESTIONS.TAB_DETAIL('apostilas', booklet?.['question-book']?.id)}
                    className="group flex items-center gap-3 rounded-[12px] border border-neutral-100 p-3 transition-colors duration-150 hover:border-neutral-200 hover:bg-neutral-50"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--color-bg-lilac)]">
                      <FiEdit3 className="h-4 w-4 text-[var(--color-secondary)]" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span
                        className="block truncate text-sm font-bold"
                        style={{ color: '#222731' }}
                      >
                        {booklet?.['question-book']?.title}
                      </span>
                      <span className="text-xs font-semibold text-neutral-400">{booklet?.['question-book']?.['question-count'] ?? 0} questões</span>
                    </span>
                    <FiChevronRight className="h-4 w-4 shrink-0 text-neutral-300 transition-transform duration-150 group-hover:translate-x-0.5" />
                  </Link>
                ))
              )}
            </div>

            <div className="px-5 pb-5">
              <button
                type="button"
                onClick={() => setShowBooklets(false)}
                className="w-full rounded-full py-2.5 text-sm font-bold text-neutral-400 transition-colors duration-150 hover:text-neutral-600"
              >
                Fechar
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
